import React from "react";
import { Box, Typography } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { lightTheme } from "../../styles/theme";
import { formatCalendarActivityDate } from "../../domain/calendar/calendarDisplayUtils";
import type { CalendarActivityItem } from "./UpcomingActivityPanel";

type CalendarActivityListProps = {
  items: CalendarActivityItem[];
  getEventColor: (noticeId: string) => string;
  onItemClick: (item: CalendarActivityItem) => void;
};

const CalendarActivityList: React.FC<CalendarActivityListProps> = ({
  items,
  getEventColor,
  onItemClick,
}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
      {items.map((item) => {
        const isPublish = item.kind === "publish";
        const eventColor = getEventColor(item.noticeId);

        return (
          <Box
            key={item.eventKey}
            component="button"
            type="button"
            onClick={() => onItemClick(item)}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              width: "100%",
              textAlign: "left",
              cursor: "pointer",
              border: "none",
              bgcolor: "transparent",
              borderRadius: 1.5,
              px: 0.5,
              py: 0.75,
              mx: -0.5,
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.25,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...(isPublish
                  ? {
                      bgcolor: lightTheme.colors.warningAmberTint20,
                      border: "2px dashed",
                      borderColor: lightTheme.colors.warning,
                      color: lightTheme.colors.warning,
                    }
                  : item.isStaffOnly
                    ? {
                        bgcolor: "#f5f0ff",
                        border: "2px dotted",
                        borderColor: lightTheme.colors.purple,
                        color: lightTheme.colors.purple,
                      }
                    : {
                        bgcolor: eventColor,
                        color: "common.white",
                      }),
              }}
            >
              {isPublish ? (
                <CampaignOutlinedIcon sx={{ fontSize: 20 }} />
              ) : (
                <EventOutlinedIcon sx={{ fontSize: 20 }} />
              )}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.3 }}
                noWrap
              >
                {item.fullTitle}
              </Typography>
              {isPublish && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "warning.main",
                    fontWeight: 600,
                    display: "block",
                  }}
                >
                  Scheduled
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {formatCalendarActivityDate(item.start, {
                  includeTime: isPublish || Boolean(item.notice.eventStartTime),
                })}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default CalendarActivityList;
